// CollectionTemplate — design spec §3.
// 2×2 grid (max 4). Independent peer items.
// Reference: Linear homepage features grid + Apple iPad Pro "Pro features."

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Eyebrow } from '../stage/Eyebrow';
import { PointableItem, DefaultOrbAnchor } from '../stage/PointableItem';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface CollectionProps {
  content: SceneContent;
}

export function CollectionTemplate({ content }: CollectionProps) {
  const { headline, eyebrow, items = [] } = content;
  const cells = items.slice(0, 4);

  return (
    <Stage>
      {/* Orb anchor — top-left, 48px per spec §3g. Hidden when the agent
          is pointing at a specific cell. */}
      <div className="absolute top-12 left-12 max-md:top-6 max-md:left-6">
        <DefaultOrbAnchor size={48} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.1)}
        className="m-auto w-full max-w-[1280px] flex flex-col gap-12"
      >
        {(eyebrow || headline) && (
          <div className="text-center flex flex-col gap-6 items-center">
            {eyebrow && (
              <motion.div variants={fadeUp}>
                <Eyebrow>{eyebrow}</Eyebrow>
              </motion.div>
            )}
            {headline && (
              <motion.h2 variants={fadeUp} className="text-headline font-display max-w-[24ch] text-balance">
                {headline}
              </motion.h2>
            )}
          </div>
        )}

        <motion.div
          variants={stagger(0.1)}
          className="grid grid-cols-2 grid-rows-2 gap-px bg-fg-faint/40 rounded-2xl overflow-hidden max-md:grid-cols-1 max-md:grid-rows-4"
        >
          {cells.map((it, i) => (
            <motion.article
              key={i}
              variants={fadeUp}
              className="bg-void-800 min-h-[280px] max-md:min-h-[200px]"
            >
              <PointableItem
                orbTargetId={`item_${i}`}
                orbSize={40}
                orbPlacement="top-right"
                className="p-12 max-md:p-8 flex flex-col gap-4 h-full"
              >
                {it.eyebrow && <Eyebrow>{it.eyebrow}</Eyebrow>}
                <h3 className="text-headline font-display text-fg">{it.headline}</h3>
                {it.body && (
                  <p className="text-body text-fg-soft mt-2 max-w-[34ch]">{it.body}</p>
                )}
              </PointableItem>
            </motion.article>
          ))}
        </motion.div>
      </motion.div>
    </Stage>
  );
}
